import { inject, Injectable } from '@angular/core';
import {
  App,
  CreateKnowledgeSourceRequestInterface,
  DeleteKnowledgeSourceRequestInterface,
  KnowledgeBaseService,
  KnowledgeSource,
  KnowledgeSourceConfigInterface,
  KnowledgeSourceConfigType,
  ListAllAppsForKnowledgeSourceRequestInterface,
  ListAllKnowledgeSourcesForAppRequestInterface,
  ListAppsResponse,
  ListKnowledgeSourcesRequestInterface,
  ListKnowledgeSourcesResponse,
  NamespaceInterface,
  PagedRequestOptionsInterface,
  UpdateKnowledgeSourceRequestInterface,
  UpsertKnowledgeForAppRequestInterface,
} from '@vendasta/embeddings';
import { catchError, firstValueFrom, throwError } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  BUSINESS_PROFILE_URL_TOKEN,
  INBOX_SETTINGS_ROUTE_TOKEN,
  MANAGE_KNOWLEDGE_URL_TOKEN,
  MARKET_ID_TOKEN,
  PARTNER_ID_TOKEN,
} from './tokens';
import { TranslateService } from '@ngx-translate/core';
import { RouteCommand } from './interface/config.interface';

@Injectable()
export class AiKnowledgeService {
  private readonly knowledgeBaseService = inject(KnowledgeBaseService);
  private readonly translateService = inject(TranslateService);

  private readonly partnerId = toSignal(inject(PARTNER_ID_TOKEN));
  private readonly marketId = toSignal(inject(MARKET_ID_TOKEN));
  private readonly accountGroupId = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));
  private readonly manageKnowledgeUrl = toSignal(inject(MANAGE_KNOWLEDGE_URL_TOKEN));
  private readonly businessProfileUrl = toSignal(inject(BUSINESS_PROFILE_URL_TOKEN));
  private readonly inboxSettingsRoute = toSignal(inject(INBOX_SETTINGS_ROUTE_TOKEN));

  async getApp(id: string, includeKnowledgeBaseKeys?: boolean): Promise<App> {
    return await firstValueFrom(
      this.knowledgeBaseService.getApp({
        id: id,
        namespace: this.buildNamespaceInterface(),
        includeKnowledgeBaseKeys: includeKnowledgeBaseKeys,
      }),
    );
  }

  async listApps(includeKnowledgeBaseKeys?: boolean): Promise<ListAppsResponse> {
    return await firstValueFrom(
      this.knowledgeBaseService.listApps({
        filters: {
          namespace: this.buildNamespaceInterface(),
        },
        includeKnowledgeBaseKeys: includeKnowledgeBaseKeys,
      }),
    );
  }

  async getKnowledgeSource(
    id: string,
    namespace: NamespaceInterface,
    includeKnowledgeBaseKeys?: boolean,
  ): Promise<KnowledgeSource> {
    return await firstValueFrom(
      this.knowledgeBaseService.getKnowledgeSource({
        id: id,
        namespace: namespace,
        includeKnowledgeBaseKeys: includeKnowledgeBaseKeys,
      }),
    );
  }

  // TODO paging
  async listKnowledgeSources(
    configTypes?: KnowledgeSourceConfigType[],
    pagingOptions?: PagedRequestOptionsInterface,
  ): Promise<ListKnowledgeSourcesResponse> {
    const request: ListKnowledgeSourcesRequestInterface = {
      filters: {
        namespace: this.buildNamespaceInterface(),
        configTypes: configTypes,
      },
      pagingOptions: pagingOptions,
    };
    return await firstValueFrom(this.knowledgeBaseService.listKnowledgeSources(request));
  }

  async updateKnowledgeSourceNameAndConfig(source: KnowledgeSource): Promise<void> {
    const request: UpdateKnowledgeSourceRequestInterface = {
      knowledgeSource: source,
      fieldMask: {
        paths: ['name', 'config'],
      },
    };
    await firstValueFrom(this.knowledgeBaseService.updateKnowledgeSource(request));
  }

  async createKnowledgeSource(name: string, config: KnowledgeSourceConfigInterface): Promise<string> {
    const request: CreateKnowledgeSourceRequestInterface = {
      knowledgeSource: {
        namespace: this.buildNamespaceInterface(),
        name: name,
        config: config,
      },
    };
    const result = await firstValueFrom(this.knowledgeBaseService.createKnowledgeSource(request));
    return result.id;
  }

  async deleteKnowledgeSource(id: string): Promise<void> {
    const request: DeleteKnowledgeSourceRequestInterface = {
      id: id,
      namespace: this.buildNamespaceInterface(),
    };
    await firstValueFrom(this.knowledgeBaseService.deleteKnowledgeSource(request));
  }

  async listAllAppsForKnowledgeSource(knowledgeSourceId: string): Promise<App[]> {
    const request: ListAllAppsForKnowledgeSourceRequestInterface = {
      knowledgeSourceId: knowledgeSourceId,
      namespace: this.buildNamespaceInterface(),
    };
    const results = await firstValueFrom(this.knowledgeBaseService.listAllAppsForKnowledgeSource(request));
    return results.apps || [];
  }

  async listAllKnowledgeSourcesForApp(
    appId: string,
    configTypes?: KnowledgeSourceConfigType[],
  ): Promise<KnowledgeSource[]> {
    const request: ListAllKnowledgeSourcesForAppRequestInterface = {
      appId: appId,
      namespace: this.buildNamespaceInterface(),
      filters: {
        configTypes: configTypes,
      },
    };
    const results = await firstValueFrom(this.knowledgeBaseService.listAllKnowledgeSourcesForApp(request));
    return results.knowledgeSources || [];
  }

  async upsertKnowledgeForApp(
    appId: string,
    name: string,
    configurationUrl: string,
    knowledgeSources?: KnowledgeSource[],
  ): Promise<void> {
    const configs = [];
    const knowledgeSourceKeys = [];

    for (const source of knowledgeSources) {
      knowledgeSourceKeys.push({
        knowledgeSourceId: source.id,
        namespace: source.namespace,
      });
    }

    const request: UpsertKnowledgeForAppRequestInterface = {
      app: {
        id: appId,
        namespace: this.buildNamespaceInterface(),
        name: name,
        configurationUrl: configurationUrl,
      },
      configs: configs,
      knowledgeSourceKeys: knowledgeSourceKeys,
    };
    await firstValueFrom(
      this.knowledgeBaseService.upsertKnowledgeForApp(request).pipe(catchError((err) => throwError(() => err))),
    );
  }

  buildNamespaceInterface(): NamespaceInterface {
    if (this.accountGroupId()) {
      return {
        accountGroupNamespace: {
          accountGroupId: this.accountGroupId(),
          marketId: this.marketId(),
          partnerId: this.partnerId(),
        },
      };
    }
    return {
      partnerNamespace: {
        partnerId: this.partnerId(),
      },
    };
  }

  buildManageKnowledgeUrl(): string {
    return this.manageKnowledgeUrl();
  }

  buildEditKnowledgeUrl(sourceId: string): string {
    return this.buildManageKnowledgeUrl() + `?editSource=${sourceId}`;
  }

  buildAddKnowledgeUrl(): string {
    return this.buildManageKnowledgeUrl() + `?addSource=true`;
  }

  buildBusinessProfileUrl(): string {
    return this.businessProfileUrl();
  }

  buildInboxSettingsRoute(): RouteCommand[] {
    return this.inboxSettingsRoute();
  }

  buildBusinessProfileKnowledgeSourceId(): string {
    if (this.accountGroupId()) {
      return `KS-business-profile-${this.accountGroupId()}`;
    }
    return '';
  }

  buildBusinessProfileKnowledgeSource(): KnowledgeSource | null {
    if (this.accountGroupId()) {
      return {
        id: this.buildBusinessProfileKnowledgeSourceId(),
        namespace: this.buildNamespaceInterface(),
        name: 'Business Profile', // untranslated because that is how the name is in the backend
        config: {
          businessProfileConfig: {
            accountGroupId: this.accountGroupId(),
          },
        },
      } as KnowledgeSource;
    }
    return null;
  }

  getKnowledgePreview(source: KnowledgeSource): string {
    if (source?.config?.businessProfileConfig?.accountGroupId) {
      return this.translateService.instant('INBOX.WEBCHAT.SETTINGS.BUSINESS_PROFILE.DESCRIPTION');
    }
    if (source?.config?.customDataConfig) {
      return source?.config?.customDataConfig?.text;
    }
    if (source?.config?.websiteScrapeConfig) {
      return source?.config?.websiteScrapeConfig?.url;
    }

    return '';
  }
}
